import { ReactElement } from 'react';
import { Config } from '../../../../types/Config';
import ConfigEntry from './ConfigEntry';
import AccordionIcon from './AccordionIcon';
import { useFormContext } from 'react-hook-form';
import { EditOperationMode, isOperationLocked } from './SessionConfigs';

interface SessionConfigsListProps {
  session: any;
  isEdit: boolean;
  configControls: any;
  showAllRuns: boolean;
  setShowAllRuns: (show: boolean) => void;
  updateNextConfigId: any;
  plannedRunConfigIds: any[];
  setPlannedRunConfigIds: any;
  nextRunIndex: number;
  activeRunId: string | null;
  setActiveRunId: (id: string | null) => void;
  invalidateSession: any;
  handleReprocessConfigChange: (configId: string, shouldReprocess: boolean) => void;
  configsToReprocess: Config[];
  reorderConfigLetters: () => void;
  configMovesToSave: { [key: string]: () => Promise<void> };
  changedConfigIds: Set<string>;
  setChangedConfigIds: (ids: Set<string>) => void;
  editOpMode: EditOperationMode;
  setEditOpMode: (mode: EditOperationMode) => void;
}

const SessionConfigsList = ({
  session,
  isEdit,
  configControls,
  showAllRuns,
  setShowAllRuns,
  updateNextConfigId,
  plannedRunConfigIds,
  setPlannedRunConfigIds,
  nextRunIndex,
  activeRunId,
  setActiveRunId,
  invalidateSession,
  handleReprocessConfigChange,
  configsToReprocess,
  reorderConfigLetters,
  configMovesToSave,
  changedConfigIds,
  setChangedConfigIds,
  editOpMode,
  setEditOpMode,
}: SessionConfigsListProps): ReactElement => {
  const configs = configControls;
  const { setValue, watch } = useFormContext();

  // Watch all config descriptions to detect duplicates in real-time
  const watchedConfigs = watch('configs') || [];

  // Find duplicate descriptions (only for non-deleted configs)
  const duplicateDescriptions = new Set<string>();
  const descriptionCounts = new Map<string, number>();

  watchedConfigs.forEach((config: any) => {
    if (!config.isDeleted && config.description && config.description.trim()) {
      const trimmedDescription = config.description.trim();
      const count = descriptionCounts.get(trimmedDescription) || 0;
      descriptionCounts.set(trimmedDescription, count + 1);
    }
  });

  descriptionCounts.forEach((count, description) => {
    if (count > 1) {
      duplicateDescriptions.add(description);
    }
  });

  // Function to swap configurations
  const handleSwapConfig = (index: number, direction: 'up' | 'down') => {
    if (isOperationLocked(editOpMode, 'reorder')) return;
    setEditOpMode('reorder');

    const currentConfigs = watch('configs');
    if (!currentConfigs) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= currentConfigs.length) return;

    // Create a copy of configs array
    const newConfigs = [...currentConfigs];

    // Swap the configurations
    [newConfigs[index], newConfigs[swapIndex]] = [newConfigs[swapIndex], newConfigs[index]];

    // Fix baseline references: if the config that moved up had the config that moved
    // down as its baseline, swap the baseline relationship
    const upperIndex = Math.min(index, swapIndex);
    const lowerIndex = Math.max(index, swapIndex);
    if (newConfigs[upperIndex].baseline_id === newConfigs[lowerIndex].id) {
      const oldUpperBaseline = newConfigs[lowerIndex].baseline_id;
      newConfigs[lowerIndex].baseline_id = newConfigs[upperIndex].id;
      newConfigs[upperIndex].baseline_id = oldUpperBaseline;
    }

    // Update form values
    setValue('configs', newConfigs, { shouldDirty: true });

    // Mark both configs as changed
    const newChangedIds = new Set(changedConfigIds);
    newChangedIds.add(newConfigs[index].id);
    newChangedIds.add(newConfigs[swapIndex].id);
    setChangedConfigIds(newChangedIds);

    // Reorder letters to maintain sequential ordering
    reorderConfigLetters();
  };

  return (
    <div className="cr-flex cr-flex-col">
      <div className="cr-grid cr-grid-cols-[0.4fr_0.5fr_0.8fr_1.2fr_1.2fr_1.2fr_0.1fr] cr-text-center md:cr-m-2">
        <div
          className="cr-col-span-7 cr-mb-4 cr-grid cr-grid-cols-subgrid cr-text-[10px] cr-font-bold cr-uppercase cr-text-cirrusBlue-400 md:cr-text-[14px]"
          style={{
            fontFamily: '\'Poppins\', sans-serif',
            fontWeight: '500',
          }}
        >
          <div className="cr-min-w-0" onClick={() => setShowAllRuns(!showAllRuns)}>
            <AccordionIcon show={showAllRuns} />
          </div>
          <div className="cr-flex cr-min-w-0 cr-flex-col cr-justify-center cr-pl-0 cr-text-center">
            <div>Cfg</div>
            <div>Run #</div>
          </div>
          <div className="cr-flex cr-min-w-0 cr-flex-col cr-justify-center cr-truncate cr-pl-0 cr-text-center">
            <div>
              <span className="cr-inline md:cr-hidden">Par</span>
              <span className="cr-hidden md:cr-inline">Parent</span>
            </div>
            <div>Time</div>
          </div>
          <div className="cr-min-w-0 cr-truncate">Drag</div>
          <div className="cr-min-w-0 cr-truncate cr-text-sm md:cr-text-base">+/-</div>
          <div className="cr-flex cr-min-w-0 cr-justify-end cr-truncate cr-pr-2">Next</div>
          <div className="cr-min-w-0 cr-truncate"></div>
        </div>
        {configs.map((config: any, index: number) => {
          const originalConfig = session.configs.find((c: any) => c?.id === config?.id);
          const watchedConfig = watchedConfigs[index];
          const trimmedDescription = watchedConfig?.description?.trim();
          const hasDuplicateDescription = trimmedDescription && duplicateDescriptions.has(trimmedDescription);

          return (
            <ConfigEntry
              index={index}
              key={config.fieldId}
              isEdit={isEdit}
              originalConfig={originalConfig}
              showAllRuns={showAllRuns}
              updateNextConfigId={updateNextConfigId}
              plannedRunConfigIds={plannedRunConfigIds}
              setPlannedRunConfigIds={setPlannedRunConfigIds}
              nextRunIndex={nextRunIndex}
              activeRunId={activeRunId}
              setActiveRunId={setActiveRunId}
              invalidateSession={invalidateSession}
              handleReprocessConfig={handleReprocessConfigChange}
              reprocessingConfigId={configsToReprocess.some((c) => c.id === config.id) ? config.id : null}
              reorderConfigLetters={reorderConfigLetters}
              configMovesToSave={configMovesToSave}
              onSwapConfig={handleSwapConfig}
              changedConfigIds={changedConfigIds}
              setChangedConfigIds={setChangedConfigIds}
              hasDuplicateDescription={hasDuplicateDescription}
              editOpMode={editOpMode}
              setEditOpMode={setEditOpMode}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SessionConfigsList;
